import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

const BRAND_BLUE = '#2558ab';
const BRAND_ORANGE = '#ff8057';

const getCleanValue = (val) => val && val.toString().trim() !== '' ? val.toString() : 'Not yet provided';

export const exportToPDF = (moduleData) => {
  try {
    const doc = new jsPDF();
    let yPos = 20;
    const pageWidth = doc.internal.pageSize.getWidth();

    // --- Header ---
    doc.setFillColor(37, 88, 171); // BRAND_BLUE
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    doc.text('Planned with Purpose', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('A Gospel-Centered Planning Workspace', pageWidth / 2, 30, { align: 'center' });

    yPos = 55;

    // --- Helper for Sections ---
    const addSection = (title, content) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setTextColor(BRAND_BLUE);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(title, 20, yPos);
      yPos += 8;
      
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      
      content.forEach(item => {
        const splitText = doc.splitTextToSize(`${item.label}: ${getCleanValue(item.value)}`, 170);
        doc.text(splitText, 25, yPos);
        yPos += (splitText.length * 5) + 3;
        
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
      });
      yPos += 5;
    };

    // Module 1
    if (moduleData.module1?.data) {
      const d = moduleData.module1.data;
      addSection('1. Clarify the Calling', [
        { label: 'Calling', value: d.callingPrompt },
        { label: 'Burden', value: d.burdenNudge },
        { label: 'Clarity Statement', value: d.clarityStatement },
        { label: 'Biblical Anchor', value: d.biblicalAnchor }
      ]);
    }

    // Module 2
    if (moduleData.module2?.data) {
      const d = moduleData.module2.data;
      const content = [{ label: 'Book Type', value: d.bookType }];
      if (d.bookType === 'nonfiction' && d.nonfictionChapters) {
        d.nonfictionChapters.forEach(ch => {
          content.push({ label: `Chapter ${ch.chapter}`, value: `${ch.bigIdea} (${ch.biblicalTruth})` });
        });
      }
      addSection('2. Book Structure', content);
    }

    // Module 3
    if (moduleData.module3?.data) {
      const d = moduleData.module3.data;
      addSection('3. Book Plan', [
        { label: 'Word Count Target', value: d.targetWordCount },
        { label: 'Timeline', value: `${d.idealStartDate} to ${d.idealFinishDate}` },
        { label: 'Rhythm', value: `${d.sessionsPerWeek} sessions/week` }
      ]);
    }

    // Module 4
    if (moduleData.module4?.data) {
      const d = moduleData.module4.data;
      addSection('4. Momentum & Grace', [
        { label: 'Reframe', value: d.reframeStatement },
        { label: 'Restart Plan', value: d.restartPlan }
      ]);
    }

    // Module 5
    if (moduleData.module5?.data) {
      const d = moduleData.module5.data;
      const paragraph = d.bookType === 'fiction' ? d.fictionData?.openingParagraph : d.nonfictionData?.openingParagraph;
      addSection('5. First Chapter Jumpstart', [
        { label: 'Opening Paragraph', value: paragraph }
      ]);
    }

    doc.save('Planned-with-Purpose-Workbook.pdf');
  } catch (error) {
    console.error('PDF Generation failed:', error);
    alert('Failed to generate PDF. Please try again.');
  }
};

export const exportToWord = async (moduleData) => {
  try {
    const sections = [];

    // Helper to add module sections
    const createModulePara = (title, fields) => {
      const paras = [
        new Paragraph({
          text: title,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        })
      ];

      fields.forEach(f => {
        paras.push(new Paragraph({
          children: [
            new TextRun({ text: `${f.label}: `, bold: true }),
            new TextRun({ text: getCleanValue(f.value) })
          ],
          spacing: { after: 120 }
        }));
      });

      return paras;
    };

    // Add all modules
    if (moduleData.module1?.data) {
      const d = moduleData.module1.data;
      sections.push(...createModulePara('1. Clarify the Calling', [
        { label: 'Calling', value: d.callingPrompt },
        { label: 'Burden', value: d.burdenNudge },
        { label: 'Clarity Statement', value: d.clarityStatement }
      ]));
    }

    if (moduleData.module3?.data) {
      const d = moduleData.module3.data;
      sections.push(...createModulePara('3. Book Plan', [
        { label: 'Target Words', value: d.targetWordCount },
        { label: 'Timeline', value: `${d.idealStartDate} to ${d.idealFinishDate}` }
      ]));
    }

    // Ensure we have at least one section
    if (sections.length === 0) {
      sections.push(new Paragraph("No modules have been started yet."));
    }

    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({ text: "Planned with Purpose", bold: true, size: 48, color: BRAND_BLUE.replace('#', '') }),
            ],
            spacing: { after: 200 }
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({ text: "A Gospel-Centered Planning Workspace", size: 24, italic: true }),
            ],
            spacing: { after: 400 }
          }),
          ...sections
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, "Planned-with-Purpose-Workbook.docx");
  } catch (error) {
    console.error('Word Generation failed:', error);
    alert('Failed to generate Word document. Please try again.');
  }
};

export const sendEmail = (moduleData) => {
  const subject = encodeURIComponent("My Planned with Purpose Workbook Summary");
  const m1 = moduleData.module1?.data?.clarityStatement || "Not yet defined";
  const m3 = moduleData.module3?.data?.targetWordCount ? `${moduleData.module3.data.targetWordCount} words` : "Not yet defined";
  
  const body = encodeURIComponent(
    `PLANNED WITH PURPOSE WORKBOOK SUMMARY\n` +
    `--------------------------------------\n\n` +
    `MY CLARITY STATEMENT:\n"${m1}"\n\n` +
    `PROJECT SCOPE:\nTarget: ${m3}\n\n` +
    `REFRAME STATEMENT:\n"${moduleData.module4?.data?.reframeStatement || 'Faithful progress matters more than perfect consistency.'}"\n\n` +
    `Generated via GospelWise Author Workspace.`
  );
  
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
};